import type { ConfigEditorKeyType } from '~/api/models/Egg';
import RequestService from './request';

/**
 * NOTE: snake_case throughout. These payloads are arrays of objects, and the
 * API parser only camelCases object keys at the top level without recursing
 * into arrays, which is also what keeps real config key names such as
 * `sv_hostname` from being rewritten on the way through.
 */
export interface ConfigEditorFileSummary {
    path: string;
    server_offline: boolean;
    help_url: string | null;
}

export interface ConfigEditorFileList {
    files: ConfigEditorFileSummary[];
    /** Discovery stopped at its budget; more files may exist than are listed. */
    truncated: boolean;
}

export interface ConfigEditorKey {
    /**
     * Section-qualified for formats with sections, e.g. `[Section]::Key`, and
     * suffixed `#2`, `#3`… past the first appearance of a repeated key.
     */
    identity: string;
    key: string;
    section: string | null;
    value: string;
    label: string;
    description: string | null;
    help_url: string | null;
    can_delete: boolean;
    type: ConfigEditorKeyType;
    options: string[] | null;
    /** False when the key was found in the file but never described by an admin. */
    declared: boolean;
    /** Which appearance of a repeated key this is, counting from one. */
    occurrence: number;
    /** The group the admin filed this key under. Null when undescribed, or described outside any named group. */
    category: string | null;
    /** Where the admin placed this key across the whole file. Null when undescribed. */
    position: number | null;
    /** The key this one was unpacked out of, for a member of an inline struct. Null for everything else. */
    parent: string | null;
}

export interface ConfigEditorFileContents {
    path: string;
    format: string;
    /** False when the file could not be read as key/value pairs; edit `raw` instead. */
    parsed: boolean;
    exists: boolean;
    server_offline: boolean;
    help_url: string | null;
    keys: ConfigEditorKey[];
    raw: string;
}

export interface UpdateConfigFileRequest {
    path: string;
    values?: Record<string, string>;
    delete?: string[];
}

class ConfigEditorService {
    list(): Promise<ConfigEditorFileList> {
        return RequestService.get('/servers/:server/config-editor');
    }

    read(path: string): Promise<ConfigEditorFileContents> {
        return RequestService.get('/servers/:server/config-editor/content', { path });
    }

    update(data: UpdateConfigFileRequest): Promise<{ message: string }> {
        return RequestService.put('/servers/:server/config-editor/content', data);
    }
}

export default new ConfigEditorService();
